import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { ChevronLeft, IconButton, LinearProgressBar, Typography } from "@coolremote/shared";
import HVACImg from "../../images/coolmaster.png";
import { DiscoveryFlowState, hasSiteReady } from "./discoveryFlowState";
import { discoverDevice } from "./NewUserFlow/deviceRegistrationPublicApi";
import parentStyles from "./DeviceRegistrationPublic.module.css";

interface DeviceDiscoveryStepProps {
  /** e.g. `/register-device/new-user` or `/register-device/existing-user`. */
  basePath: string;
  /** Redirect target when a site hasn't been resolved yet. */
  previousStepPath: string;
}

/**
 * Device discovery with a progress bar — step 4 of both branches. Built for
 * the new-user branch in CM-618; shared verbatim with the existing-user
 * branch (CM-619) via `basePath`/`previousStepPath`, since the only
 * difference between the branches is which routes lead here and back.
 */
const DeviceDiscoveryStep: React.FC<DeviceDiscoveryStepProps> = ({ basePath, previousStepPath }) => {
  const history = useHistory<DiscoveryFlowState | undefined>();
  const deviceState = history.location.state;

  useEffect(() => {
    if (!hasSiteReady(deviceState) || !deviceState.deviceId) {
      history.replace(previousStepPath);
      return;
    }

    discoverDevice(deviceState.deviceId, deviceState.serial).then((result) => {
      // Attach the result to this entry too, so backing into this step from
      // Congratulations restores it instead of re-running discovery.
      history.replace(`${basePath}/discovery`, {
        ...deviceState,
        discoveryResult: result
      });
      history.push(`${basePath}/congratulations`, {
        ...deviceState,
        discoveryResult: result
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasSiteReady(deviceState) || !deviceState.deviceId) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Connect Device`}</span>
        <span />
      </div>

      <div className={parentStyles.discoveryContent}>
        <Typography variant="pageTitle">{t`Connect Your Device`}</Typography>
        <img src={HVACImg} alt={t`HVAC device`} className={parentStyles.discoveryDevice} />
        <Typography className={parentStyles.discoveryCaption}>{t`Discovering connected HVAC units`}</Typography>
        <Typography variant="meta">{t`Just a moment`}</Typography>
        <LinearProgressBar variant="indeterminate" className={parentStyles.progressBar} />
      </div>
    </div>
  );
};

export default DeviceDiscoveryStep;
